import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  endorsementFinancial,
  type EndorsementDetail,
  type PolicyDetail,
} from '@/shared/api/resources';
import {
  formatDateTime,
  formatMoney,
  formatRate,
  moneyInputError,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { Modal } from '@/shared/ui/Modal';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Textarea } from '@/shared/ui/Textarea';
import { useToast } from '@/shared/ui/useToast';

import { EndorsementInfo } from './EndorsementPolicyDetails';
import { regrasEmTexto } from './endossoFinanceiro';

/**
 * Pecas do endosso com efeito financeiro (P-END-2) no passo "Informacoes do endosso":
 * as cosseguradoras da apolice (END-057, END-062), o premio estimado com o valor
 * solicitado (END-064 a END-068) e a revisao de valor (END-069 a END-071).
 *
 * Nada aqui calcula. O premio estimado a cobrar ou a restituir e regra pendente: a tela
 * mostra a regra que falta, e o valor aparece quando ela for homologada.
 */

/** Secao das cosseguradoras da apolice (END-057), com a marca da lider. */
export function EndorsementCoinsurersCard({ policy }: { policy: PolicyDetail }) {
  const cosseguradoras = policy.coinsurers ?? [];
  return (
    <Card>
      <CardHeader
        title="Cosseguradoras"
        description="As cosseguradoras da apolice. O efeito do endosso na distribuicao do cosseguro e regra pendente (RES-005)."
      />
      <CardBody>
        {cosseguradoras.length === 0 ? (
          <p className="text-sm text-ink-600">Nenhuma cosseguradora informada</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-ink-900">
            {cosseguradoras.map((item) => (
              <li key={item.id}>
                {item.name}
                {item.is_leader ? ' (Lider)' : ''} - {formatRate(item.share_bps)}
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  );
}

/**
 * Premio estimado do endosso (END-064, END-065, END-067), valor solicitado (END-066) e
 * revisao de valor (END-069 a END-071, END-077).
 */
export function EndorsementPremiumEstimateCard({
  endorsement,
  editavel,
  podePedir,
  valorSolicitado,
  onValorSolicitado,
}: {
  endorsement: EndorsementDetail;
  editavel: boolean;
  podePedir: boolean;
  valorSolicitado: string;
  onValorSolicitado: (valor: string) => void;
}) {
  const [revisando, setRevisando] = useState(false);
  const codigo = endorsement.endorsement_type?.code ?? 'alteracao';
  const financeiro = endorsement.financial ?? null;
  const cancelamento = codigo === 'cancelamento';
  const emAndamento = ['rascunho', 'solicitado', 'em_analise'].includes(
    endorsement.status,
  );

  return (
    <Card>
      <CardHeader
        title="Premio Estimado"
        description={`O novo calculo do premio estimado a ${cancelamento ? 'restituir' : 'cobrar'} e baseado na diferenca entre o valor da apolice vigente e a politica da seguradora.`}
      />
      <CardBody className="flex flex-col gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <EndorsementInfo
            rotulo={
              cancelamento ? 'Premio estimado a restituir' : 'Premio estimado a cobrar'
            }
            valor={
              cancelamento && financeiro?.estimated_refund
                ? formatMoney(financeiro.estimated_refund)
                : `Regra pendente (${regrasEmTexto(financeiro?.premium_rules)})`
            }
          />
          <MoneyField
            label="Valor solicitado"
            disabled={!editavel}
            value={valorSolicitado}
            error={moneyInputError(valorSolicitado)}
            onValueChange={onValorSolicitado}
          />
        </div>
        <Alert tone="warning" title="Calculo bloqueado ate a homologacao">
          {financeiro?.premium_rules.message ??
            'O premio do endosso e a restituicao sao regra pendente.'}{' '}
          Quando a regra de atraso de pagamento entrar (END-002), o aviso &quot;Os
          valores de premios pagos ate o momento nao sao suficientes para cobrir o
          periodo de risco. A proposta seguira para analise da Subscricao.&quot; leva a
          cotacao para a subscricao.
        </Alert>
        {financeiro?.review_requested ? (
          <Alert tone="info" title="Revisao de valor solicitada">
            Valor sugerido{' '}
            {financeiro.review_suggested
              ? formatMoney(financeiro.review_suggested)
              : ''}
            , por {financeiro.review_requested_label || 'quem pediu'}
            {financeiro.review_requested_at
              ? ` em ${formatDateTime(financeiro.review_requested_at)}`
              : ''}
            . Justificativa: {financeiro.review_justification}
          </Alert>
        ) : null}
        {podePedir && emAndamento && financeiro ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setRevisando(true)}>
              Revisar valor
            </Button>
          </div>
        ) : null}
      </CardBody>
      <EndorsementReviewDialog
        key={revisando ? 'aberto' : 'fechado'}
        open={revisando}
        endorsement={endorsement}
        onClose={() => setRevisando(false)}
      />
    </Card>
  );
}

/** Dialogo "Solicitar revisao" (END-069 a END-071). */
function EndorsementReviewDialog({
  open,
  endorsement,
  onClose,
}: {
  open: boolean;
  endorsement: EndorsementDetail;
  onClose: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [valor, setValor] = useState('');
  const [justificativa, setJustificativa] = useState('');

  const pedir = useMutation({
    mutationFn: () =>
      endorsementFinancial.premiumReview(endorsement.id, {
        suggested_amount: toMoney(valor),
        justification: justificativa,
      }),
    onSuccess: (detalhe) => {
      queryClient.setQueryData(['endorsement', endorsement.id], detalhe);
      toast.success('Solicitacao salva com sucesso');
      onClose();
    },
  });
  const invalido =
    valor.trim() === '' ||
    moneyInputError(valor) !== undefined ||
    justificativa.trim() === '';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Solicitar revisao"
      description="Preencha os dados para solicitar a revisao da apolice"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            loading={pedir.isPending}
            disabled={invalido}
            onClick={() => pedir.mutate()}
          >
            Solicitar revisao
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {pedir.isError ? (
          <Alert tone="danger" title="Nao foi possivel solicitar a revisao">
            {pedir.error.message}
          </Alert>
        ) : null}
        <MoneyField
          label="Valor sugerido"
          required
          value={valor}
          error={moneyInputError(valor)}
          onValueChange={setValor}
        />
        <Textarea
          label="Justificativa para solicitacao de revisao"
          required
          maxLength={2000}
          value={justificativa}
          onChange={(event) => setJustificativa(event.target.value)}
        />
      </div>
    </Modal>
  );
}
